/**
 * HTML Cleaner
 * Main orchestration module for cleaning Microsoft Word HTML
 * Coordinates style removal, list conversion, and content preservation
 */

import {
  removeMsoStyles,
  removeMsoClasses,
  removeConditionalComments,
  removeOfficeElements,
  removeNamespaceDeclarations,
  removeUnwantedInlineStyles,
} from "./styleRemover";
import {
  detectWordListItems,
  convertWordListsToHtml,
  containsWordLists,
} from "./listParser";

/**
 * Configuration options for Word HTML cleaning
 */
export interface WordPasteCleanOptions {
  /** Remove all MSO-specific inline styles */
  removeStyles: boolean;
  /** Preserve bold formatting (<b>, <strong>, font-weight: bold) */
  preserveBold: boolean;
  /** Preserve italic formatting (<i>, <em>, font-style: italic) */
  preserveItalic: boolean;
  /** Preserve underline formatting (<u>, text-decoration: underline) */
  preserveUnderline: boolean;
  /** Preserve strikethrough formatting (<s>, <strike>, text-decoration: line-through) */
  preserveStrikethrough: boolean;
  /** Preserve hyperlinks (<a> with href) */
  preserveLinks: boolean;
  /** Preserve images (<img> tags) */
  preserveImages: boolean;
  /** Preserve table structure */
  preserveTables: boolean;
  /** Convert Word lists to proper HTML lists */
  preserveLists: boolean;
  /** Attempt to detect and preserve heading levels */
  preserveHeadings: boolean;
}

/**
 * Default options for Word HTML cleaning
 */
export const defaultCleanOptions: WordPasteCleanOptions = {
  removeStyles: true,
  preserveBold: true,
  preserveItalic: true,
  preserveUnderline: true,
  preserveStrikethrough: true,
  preserveLinks: true,
  preserveImages: true,
  preserveTables: true,
  preserveLists: true,
  preserveHeadings: true,
};

/**
 * Indicators that HTML originated from Microsoft Word
 */
const WORD_INDICATORS = [
  'class="Mso',
  "class='Mso",
  'style="mso-',
  "style='mso-",
  "<!--[if ",
  "xmlns:o=",
  "xmlns:w=",
  "<o:p>",
  "<w:",
  "urn:schemas-microsoft-com:office",
];

/**
 * Detects if HTML content originated from Microsoft Word
 * @param html - HTML string to check
 * @returns true if the HTML appears to be from Word
 */
export function isWordHTML(html: string): boolean {
  return WORD_INDICATORS.some((indicator) => html.includes(indicator));
}

/**
 * Detects and converts bold formatting from Word styles to semantic HTML
 */
function processBoldFormatting(html: string, preserve: boolean): string {
  if (!preserve) {
    // Remove bold tags entirely
    html = html.replace(/<\/?(?:b|strong)>/gi, "");
    return html;
  }

  // Convert font-weight: bold/700+ to <strong>
  html = html.replace(
    /<span([^>]*)style="([^"]*font-weight:\s*(?:bold|[7-9]00)[^"]*)"([^>]*)>([\s\S]*?)<\/span>/gi,
    (match, before, style, after, content) => {
      // Remove the font-weight from the style
      const newStyle = style
        .replace(/font-weight:\s*(?:bold|[7-9]00);?/gi, "")
        .trim();
      const styleAttr = newStyle ? ` style="${newStyle}"` : "";
      return `<strong${styleAttr}>${content}</strong>`;
    }
  );

  return html;
}

/**
 * Detects and converts italic formatting from Word styles to semantic HTML
 */
function processItalicFormatting(html: string, preserve: boolean): string {
  if (!preserve) {
    html = html.replace(/<\/?(?:i|em)>/gi, "");
    return html;
  }

  // Convert font-style: italic to <em>
  html = html.replace(
    /<span([^>]*)style="([^"]*font-style:\s*italic[^"]*)"([^>]*)>([\s\S]*?)<\/span>/gi,
    (match, before, style, after, content) => {
      const newStyle = style.replace(/font-style:\s*italic;?/gi, "").trim();
      const styleAttr = newStyle ? ` style="${newStyle}"` : "";
      return `<em${styleAttr}>${content}</em>`;
    }
  );

  return html;
}

/**
 * Detects and converts underline formatting from Word styles to semantic HTML
 */
function processUnderlineFormatting(html: string, preserve: boolean): string {
  if (!preserve) {
    html = html.replace(/<\/?u>/gi, "");
    return html;
  }

  // Convert text-decoration: underline to <u>
  html = html.replace(
    /<span([^>]*)style="([^"]*text-decoration:[^;]*underline[^"]*)"([^>]*)>([\s\S]*?)<\/span>/gi,
    (match, before, style, after, content) => {
      const newStyle = style
        .replace(/text-decoration:[^;]*underline[^;]*;?/gi, "")
        .trim();
      const styleAttr = newStyle ? ` style="${newStyle}"` : "";
      return `<u${styleAttr}>${content}</u>`;
    }
  );

  return html;
}

/**
 * Detects and converts strikethrough formatting from Word styles to semantic HTML
 */
function processStrikethroughFormatting(html: string, preserve: boolean): string {
  if (!preserve) {
    html = html.replace(/<\/?(?:s|strike|del)>/gi, "");
    return html;
  }

  // Convert text-decoration: line-through to <s>
  html = html.replace(
    /<span([^>]*)style="([^"]*text-decoration:[^;]*line-through[^"]*)"([^>]*)>([\s\S]*?)<\/span>/gi,
    (match, before, style, after, content) => {
      const newStyle = style
        .replace(/text-decoration:[^;]*line-through[^;]*;?/gi, "")
        .trim();
      const styleAttr = newStyle ? ` style="${newStyle}"` : "";
      return `<s${styleAttr}>${content}</s>`;
    }
  );

  return html;
}

/**
 * Cleans hyperlinks, optionally removing them
 */
function processLinks(html: string, preserve: boolean): string {
  if (!preserve) {
    // Remove links but keep their text content
    html = html.replace(/<a[^>]*>([\s\S]*?)<\/a>/gi, "$1");
    return html;
  }

  // Clean link attributes, keeping only href
  html = html.replace(/<a([^>]*)>/gi, (match, attrs) => {
    const hrefMatch = attrs.match(/href=["']([^"']*)["']/i);
    if (hrefMatch) {
      return `<a href="${hrefMatch[1]}">`;
    }
    return match;
  });

  return html;
}

/**
 * Cleans images, optionally removing them
 */
function processImages(html: string, preserve: boolean): string {
  if (!preserve) {
    html = html.replace(/<img[^>]*\/?>/gi, "");
    return html;
  }

  // Clean image attributes, keeping src and alt
  html = html.replace(/<img([^>]*)>/gi, (match, attrs) => {
    const srcMatch = attrs.match(/src=["']([^"']*)["']/i);
    const altMatch = attrs.match(/alt=["']([^"']*)["']/i);

    if (srcMatch) {
      const src = srcMatch[1];
      const alt = altMatch ? altMatch[1] : "";
      return `<img src="${src}" alt="${alt}" />`;
    }
    return "";
  });

  return html;
}

/**
 * Cleans table structure while preserving content
 */
function processTables(html: string, preserve: boolean): string {
  if (!preserve) {
    // Convert tables to plain paragraphs
    html = html.replace(/<table[^>]*>[\s\S]*?<\/table>/gi, (match) => {
      // Extract cell contents
      const cells: string[] = [];
      const cellRegex = /<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi;
      let cellMatch;
      while ((cellMatch = cellRegex.exec(match)) !== null) {
        const content = cellMatch[1].trim();
        if (content) cells.push(content);
      }
      return cells.map((c) => `<p>${c}</p>`).join("");
    });
    return html;
  }

  // Clean table, row, and cell attributes
  html = html.replace(/<table[^>]*>/gi, "<table>");
  html = html.replace(/<tbody[^>]*>/gi, "<tbody>");
  html = html.replace(/<thead[^>]*>/gi, "<thead>");
  html = html.replace(/<tr[^>]*>/gi, "<tr>");

  // Clean td/th but preserve colspan/rowspan
  html = html.replace(/<(t[dh])([^>]*)>/gi, (match, tag, attrs) => {
    const colspan = attrs.match(/colspan=["']?(\d+)["']?/i);
    const rowspan = attrs.match(/rowspan=["']?(\d+)["']?/i);

    let newAttrs = "";
    if (colspan && parseInt(colspan[1], 10) > 1) {
      newAttrs += ` colspan="${colspan[1]}"`;
    }
    if (rowspan && parseInt(rowspan[1], 10) > 1) {
      newAttrs += ` rowspan="${rowspan[1]}"`;
    }

    return `<${tag}${newAttrs}>`;
  });

  return html;
}

/**
 * Detects Word heading styles and converts to proper heading tags
 */
function processHeadings(html: string, preserve: boolean): string {
  if (!preserve) return html;

  // Word headings often have classes like "MsoHeading1", "MsoHeading2", etc.
  // or styles with specific font sizes

  // Convert MsoHeading classes to heading tags
  html = html.replace(
    /<p[^>]*class="[^"]*MsoHeading(\d)[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
    (match, level, content) => {
      const headingLevel = Math.min(Math.max(parseInt(level, 10), 1), 6);
      return `<h${headingLevel}>${content.trim()}</h${headingLevel}>`;
    }
  );

  // Also check for Word's Heading style in the style attribute
  html = html.replace(
    /<p[^>]*style="[^"]*mso-style-name:\s*["']?Heading\s*(\d)["']?[^"]*"[^>]*>([\s\S]*?)<\/p>/gi,
    (match, level, content) => {
      const headingLevel = Math.min(Math.max(parseInt(level, 10), 1), 6);
      return `<h${headingLevel}>${content.trim()}</h${headingLevel}>`;
    }
  );

  return html;
}

/**
 * Removes empty and whitespace-only elements
 */
function removeEmptyElements(html: string): string {
  // Remove empty spans
  html = html.replace(/<span[^>]*>\s*<\/span>/gi, "");

  // Remove empty paragraphs (but keep <br> as empty line markers)
  html = html.replace(/<p[^>]*>\s*<\/p>/gi, "");

  // Remove empty divs
  html = html.replace(/<div[^>]*>\s*<\/div>/gi, "");

  // Remove empty list items
  html = html.replace(/<li[^>]*>\s*<\/li>/gi, "");

  // Remove empty bold/italic/etc tags
  html = html.replace(/<(strong|em|b|i|u|s)[^>]*>\s*<\/\1>/gi, "");

  return html;
}

/**
 * Cleans up whitespace in HTML
 */
function cleanWhitespace(html: string): string {
  // Normalize multiple spaces to single space
  html = html.replace(/\s+/g, " ");

  // Remove spaces around block elements
  html = html.replace(/>\s+</g, "><");

  // But add back newlines for readability
  html = html.replace(/(<\/(?:p|div|h[1-6]|ul|ol|li|table|tr|td|th|blockquote)>)/gi, "$1\n");
  html = html.replace(/(<(?:p|div|h[1-6]|ul|ol|table|blockquote)[^>]*>)/gi, "\n$1");

  // Clean up multiple newlines
  html = html.replace(/\n+/g, "\n");

  return html.trim();
}

/**
 * Unwraps unnecessary span elements that have no semantic meaning
 */
function unwrapEmptySpans(html: string): string {
  // Remove spans with no attributes
  html = html.replace(/<span>([\s\S]*?)<\/span>/gi, "$1");

  // Remove spans with only whitespace in attributes
  html = html.replace(/<span\s+>([\s\S]*?)<\/span>/gi, "$1");

  return html;
}

/**
 * Main function to clean Microsoft Word HTML
 * @param html - HTML string from Word
 * @param options - Cleaning options
 * @returns Cleaned HTML suitable for TipTap
 */
export function cleanWordHTML(
  html: string,
  options: Partial<WordPasteCleanOptions> = {}
): string {
  // Merge with default options
  const opts: WordPasteCleanOptions = { ...defaultCleanOptions, ...options };

  // Quick check - if not Word HTML, return as-is
  if (!isWordHTML(html)) {
    return html;
  }

  let cleaned = html;

  // Step 1: Process lists BEFORE removing conditional comments
  // (We need the conditional comments to detect bullet vs numbered)
  if (opts.preserveLists && containsWordLists(cleaned)) {
    const listItems = detectWordListItems(cleaned);
    if (listItems.length > 0) {
      cleaned = convertWordListsToHtml(cleaned, listItems);
    }
  }

  // Step 2: Remove conditional comments (now safe since lists are processed)
  cleaned = removeConditionalComments(cleaned);

  // Step 3: Remove Office-specific elements
  cleaned = removeOfficeElements(cleaned);

  // Step 4: Remove namespace declarations
  cleaned = removeNamespaceDeclarations(cleaned);

  // Step 5: Process formatting before removing styles
  cleaned = processBoldFormatting(cleaned, opts.preserveBold);
  cleaned = processItalicFormatting(cleaned, opts.preserveItalic);
  cleaned = processUnderlineFormatting(cleaned, opts.preserveUnderline);
  cleaned = processStrikethroughFormatting(cleaned, opts.preserveStrikethrough);

  // Step 6: Process headings
  cleaned = processHeadings(cleaned, opts.preserveHeadings);

  // Step 7: Remove MSO styles and classes
  if (opts.removeStyles) {
    cleaned = removeMsoStyles(cleaned);
    cleaned = removeMsoClasses(cleaned);
    cleaned = removeUnwantedInlineStyles(cleaned);
  }

  // Step 8: Process links and images
  cleaned = processLinks(cleaned, opts.preserveLinks);
  cleaned = processImages(cleaned, opts.preserveImages);

  // Step 9: Process tables
  cleaned = processTables(cleaned, opts.preserveTables);

  // Step 10: Clean up empty elements and whitespace
  cleaned = unwrapEmptySpans(cleaned);
  cleaned = removeEmptyElements(cleaned);
  cleaned = cleanWhitespace(cleaned);

  // Step 11: Final cleanup - remove <p> tags inside <li> (TipTap list items don't need them)
  cleaned = cleaned.replace(
    /<li>\s*<p[^>]*>([\s\S]*?)<\/p>\s*<\/li>/gi,
    "<li>$1</li>"
  );

  return cleaned;
}
